if (!customElements.get('gallery-wall')) {
  class GalleryWall extends HTMLElement {
    constructor() {
      super();
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onBackdropClick = this.onBackdropClick.bind(this);
      this.onFilterClick = this.onFilterClick.bind(this);
      this.onWallClick = this.onWallClick.bind(this);
      this.activeSport = 'all';
    }

    connectedCallback() {
      this.lightbox = this.querySelector('.gallery-lightbox');
      this.filterButtons = Array.from(this.querySelectorAll('[data-gallery-filter]'));

      this.addEventListener('click', this.onWallClick);

      this.filterButtons.forEach((button) => {
        button.addEventListener('click', this.onFilterClick);
      });

      this.refreshTriggers();

      if (this.lightbox) {
        this.lightbox.addEventListener('click', this.onBackdropClick);
        this.lightbox
          .querySelector('[data-gallery-close]')
          ?.addEventListener('click', () => this.closeLightbox());
        this.lightbox
          .querySelector('[data-gallery-prev]')
          ?.addEventListener('click', () => this.showRelative(-1));
        this.lightbox
          .querySelector('[data-gallery-next]')
          ?.addEventListener('click', () => this.showRelative(1));
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onWallClick);
      document.removeEventListener('keydown', this.onKeyDown);
      document.body.classList.remove('overflow-hidden');
    }

    refreshTriggers() {
      this.triggers = Array.from(
        this.querySelectorAll('.gallery-wall__slide:not([hidden]) [data-gallery-index]')
      );
      this.triggers.forEach((trigger, index) => {
        trigger.setAttribute('data-gallery-index', String(index));
      });
    }

    onWallClick(event) {
      const trigger = event.target.closest('[data-gallery-index]');
      if (!trigger || !this.contains(trigger)) return;
      if (trigger.closest('.gallery-lightbox')) return;

      const index = Number(trigger.getAttribute('data-gallery-index'));
      if (Number.isNaN(index)) return;
      this.openLightbox(index);
    }

    onFilterClick(event) {
      const button = event.currentTarget;
      const sport = button.getAttribute('data-gallery-filter') || 'all';
      this.activeSport = sport;

      this.filterButtons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.applyFilter();
    }

    applyFilter() {
      this.querySelectorAll('.gallery-wall__slide').forEach((slide) => {
        const sport = slide.getAttribute('data-gallery-sport') || 'baseball';
        const match = this.activeSport === 'all' || sport === this.activeSport;
        slide.hidden = !match;
      });

      this.refreshTriggers();
    }

    openLightbox(index) {
      if (!this.lightbox) return;
      this.refreshTriggers();
      if (!this.triggers.length) return;
      this.currentIndex = Math.max(0, Math.min(index, this.triggers.length - 1));
      this.renderLightbox();
      this.lightbox.setAttribute('open', '');
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeyDown);
      this.lightbox.querySelector('[data-gallery-close]')?.focus();
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.removeAttribute('open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeyDown);
      const active = this.triggers[this.currentIndex];
      active?.focus();
    }

    showRelative(delta) {
      if (!this.triggers.length) return;
      const nextIndex = (this.currentIndex + delta + this.triggers.length) % this.triggers.length;
      this.currentIndex = nextIndex;
      this.renderLightbox();
    }

    showAt(index) {
      this.currentIndex = index;
      this.renderLightbox();
    }

    renderLightbox() {
      const trigger = this.triggers[this.currentIndex];
      if (!trigger || !this.lightbox) return;

      const fullSrc = trigger.getAttribute('data-gallery-full') || '';
      const alt = trigger.getAttribute('data-gallery-alt') || '';
      const image = this.lightbox.querySelector('[data-gallery-image]');
      const peekPrev = this.lightbox.querySelector('[data-gallery-peek-prev]');
      const peekNext = this.lightbox.querySelector('[data-gallery-peek-next]');
      const status = this.lightbox.querySelector('[data-gallery-status]');
      const dots = this.lightbox.querySelector('[data-gallery-dots]');
      const prevTrigger =
        this.triggers[(this.currentIndex - 1 + this.triggers.length) % this.triggers.length];
      const nextTrigger = this.triggers[(this.currentIndex + 1) % this.triggers.length];

      if (image) {
        image.setAttribute('src', fullSrc);
        image.setAttribute('alt', alt);
      }

      if (peekPrev && prevTrigger) {
        peekPrev.setAttribute('src', prevTrigger.getAttribute('data-gallery-full') || '');
      }

      if (peekNext && nextTrigger) {
        peekNext.setAttribute('src', nextTrigger.getAttribute('data-gallery-full') || '');
      }

      if (status) {
        status.textContent = `Image ${this.currentIndex + 1} of ${this.triggers.length}`;
      }

      if (dots) {
        dots.innerHTML = '';
        this.triggers.forEach((_, i) => {
          const li = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'gallery-lightbox__dot';
          button.setAttribute('aria-label', `Go to image ${i + 1}`);
          if (i === this.currentIndex) button.setAttribute('aria-current', 'true');
          button.addEventListener('click', () => this.showAt(i));
          li.appendChild(button);
          dots.appendChild(li);
        });
      }
    }

    onKeyDown(event) {
      if (!this.lightbox?.hasAttribute('open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeLightbox();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showRelative(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showRelative(1);
      }
    }

    onBackdropClick(event) {
      if (event.target === this.lightbox) {
        this.closeLightbox();
      }
    }
  }

  customElements.define('gallery-wall', GalleryWall);
}
